import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './NotificationSetting.module.css';
import { Toggle, Checkbox, Button } from '@resideo/blueprint-oneportal';
import type { SchedulerEvent } from '@resideo/blueprint-react';
import { NotificationVideoEvent, NotificationAudioEvent } from './constant';

interface NotificationSelections {
    email: string[];
    push: string[];
}

interface NotificationNavigationState {
    notificationSchedule?: SchedulerEvent[];
    notificationSelections?: Partial<NotificationSelections>;
    notificationsEnabled?: boolean;
    activeSection?: string;
}

interface EventTableProps {
    titleKey: string;
    events: string[];
    selectedEmail: string[];
    selectedPush: string[];
    onToggleEmail: (event: string) => void;
    onTogglePush: (event: string) => void;
    onSelectAllEmail: (events: string[], checked: boolean) => void;
    onSelectAllPush: (events: string[], checked: boolean) => void;
}

const WEEK_DISPLAY_DAYS = [
    { key: 'mon', day: 1 },
    { key: 'tue', day: 2 },
    { key: 'wed', day: 3 },
    { key: 'thu', day: 4 },
    { key: 'fri', day: 5 },
    { key: 'sat', day: 6 },
    { key: 'sun', day: 0 },
] as const;

function timeToFraction(time: string): number {
    const [hour = 0, minute = 0, second = 0] = time.split(':').map(Number);
    return Math.min(1, (hour * 3600 + minute * 60 + second) / 86400);
}

const EventTable = ({
    titleKey,
    events,
    selectedEmail,
    selectedPush,
    onToggleEmail,
    onTogglePush,
    onSelectAllEmail,
    onSelectAllPush,
}: EventTableProps) => {
    const { t } = useTranslation();
    const eventLabelKeys: Record<string, string> = {
        People: 'people',
        Vehicle: 'vehicle',
        Animal: 'animal',
        Package: 'package',
        Motion: 'motion',
        GlassBreak: 'glassBreak',
        'Smoke Alarm': 'smokeAlarm',
        'Car Alarm': 'carAlarm',
    };
    const allEmailChecked = events.length > 0 && events.every(event => selectedEmail.includes(event));
    const someEmailChecked = events.some(event => selectedEmail.includes(event));
    const allPushChecked = events.length > 0 && events.every(event => selectedPush.includes(event));
    const somePushChecked = events.some(event => selectedPush.includes(event));

    return (
        <div className={styles.table} role="table" aria-label={t(titleKey)}>
            {/* Header row */}
            <div className={styles.tableHeader} role="row">
                <div className={`${styles.eventNameCell} ${styles.header}`} role="columnheader">
                    {t(titleKey)}
                </div>
                <div className={styles.checkboxCell} role="columnheader">
                    <Checkbox
                        name="email-header"
                        label={t('bulkEditSettings.notifications.email')}
                        className={styles.checkboxCellLabel}
                        aria-label={t('bulkEditSettings.notifications.selectAllEmail', { title: t(titleKey) })}
                        checked={allEmailChecked}
                        indeterminate={someEmailChecked && !allEmailChecked}
                        onChange={(e) => onSelectAllEmail(events, e.target.checked)}
                    />
                </div>
                <div className={styles.checkboxCell} role="columnheader">
                    <Checkbox
                        name="push-header"
                        label={t('bulkEditSettings.notifications.push')}
                        className={styles.checkboxCellLabel}
                        aria-label={t('bulkEditSettings.notifications.selectAllPush', { title: t(titleKey) })}
                        checked={allPushChecked}
                        indeterminate={somePushChecked && !allPushChecked}
                        onChange={(e) => onSelectAllPush(events, e.target.checked)}
                    />
                </div>
            </div>
            {/* Data rows */}
            {events.map((event) => (
                <div key={event} className={styles.tableRow} role="row">
                    <div className={styles.eventNameCell} role="rowheader">
                        {t(`bulkEditSettings.notifications.events.${eventLabelKeys[event]}`)}
                    </div>
                    <div className={styles.checkboxCell} role="cell">
                        <Checkbox
                            key={event}
                            name={`email-${event}`}
                            aria-label={t('bulkEditSettings.notifications.eventChannel', {
                                event: t(`bulkEditSettings.notifications.events.${eventLabelKeys[event]}`),
                                channel: t('bulkEditSettings.notifications.email'),
                            })}
                            checked={selectedEmail.includes(event)}
                            onChange={() => onToggleEmail(event)}
                        />
                    </div>
                    <div className={styles.checkboxCell} role="cell">
                        <Checkbox
                            name={`push-${event}`}
                            aria-label={t('bulkEditSettings.notifications.eventChannel', {
                                event: t(`bulkEditSettings.notifications.events.${eventLabelKeys[event]}`),
                                channel: t('bulkEditSettings.notifications.push'),
                            })}
                            checked={selectedPush.includes(event)}
                            onChange={() => onTogglePush(event)}
                        />
                    </div>
                </div>
            ))}
        </div>
    )
};

const NotificationSetting = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const navigationState = location.state as NotificationNavigationState | null;
    const [isConfigureToggle, setIsConfigureToggle] = useState(navigationState?.notificationsEnabled ?? true);
    const [selectedEmail, setSelectedEmail] = useState<string[]>(navigationState?.notificationSelections?.email ?? []);
    const [selectedPush, setSelectedPush] = useState<string[]>(navigationState?.notificationSelections?.push ?? []);
    const savedSchedule = navigationState?.notificationSchedule ?? [];

    const handleToggle = (checked: boolean) => {
        setIsConfigureToggle(checked);
    };

    const toggleSelection = (current: string[], event: string) => (
        current.includes(event)
            ? current.filter(selectedEvent => selectedEvent !== event)
            : [...current, event]
    );

    const handleSelectAll = (setSelected: (events: string[] | ((previous: string[]) => string[])) => void) => (
        events: string[],
        checked: boolean,
    ) => {
        setSelected(previous => checked
            ? [...new Set([...previous, ...events])]
            : previous.filter(event => !events.includes(event))
        );
    };

    const openScheduleEditor = () => {
        const cameras = searchParams.get('cameras') ?? '';
        navigate(`/camera/bulk-edit/notification-schedule?cameras=${encodeURIComponent(cameras)}`, {
            state: {
                activeSection: 'notifications',
                notificationSelections: { email: selectedEmail, push: selectedPush },
                notificationsEnabled: isConfigureToggle,
                notificationSchedule: savedSchedule,
            },
        });
    };

    return (
        <>
            <div className={styles.container}>
                <div className={styles.NotificationContainer}>
                    <div className={styles.Notificationheader}>
                        <h2 className={styles.header}>{t('bulkEditSettings.notifications.configureTitle')}</h2>
                        <Toggle
                            aria-label={t('bulkEditSettings.notifications.configureTitle')}
                            checked={isConfigureToggle}
                            onChange={(e) => handleToggle(e.target.checked)}
                        />
                    </div>
                    <p className={styles.description}>{t('bulkEditSettings.notifications.emergencyDescription')}</p>
                    <p className={styles.description}>{t('bulkEditSettings.notifications.pushDescription')}</p>
                </div>
                <div className={styles.NotificationInfoBanner}>
                    <span className={styles.NotificationInfoBannerIcon} aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <circle cx="10" cy="10" r="9" stroke="#4561af" strokeWidth="2" />
                            <rect x="9" y="9" width="2" height="6" rx="1" fill="#4561af" />
                            <rect x="9" y="5" width="2" height="2" rx="1" fill="#4561af" />
                        </svg>
                    </span>
                    <p className={styles.infoText}>
                        <strong>{t('bulkEditSettings.notifications.note')}</strong>
                        {' '}
                        <span>{t('bulkEditSettings.notifications.otherUsersNote')}</span>
                    </p>
                </div>
                <div className={styles.tablesContainer}>
                    <EventTable
                        titleKey="bulkEditSettings.notifications.videoEvents"
                        events={NotificationVideoEvent}
                        selectedEmail={selectedEmail}
                        selectedPush={selectedPush}
                        onToggleEmail={event => setSelectedEmail(prev => toggleSelection(prev, event))}
                        onTogglePush={event => setSelectedPush(prev => toggleSelection(prev, event))}
                        onSelectAllEmail={handleSelectAll(setSelectedEmail)}
                        onSelectAllPush={handleSelectAll(setSelectedPush)}
                    />
                    <EventTable
                        titleKey="bulkEditSettings.notifications.audioEvents"
                        events={NotificationAudioEvent}
                        selectedEmail={selectedEmail}
                        selectedPush={selectedPush}
                        onToggleEmail={event => setSelectedEmail(prev => toggleSelection(prev, event))}
                        onTogglePush={event => setSelectedPush(prev => toggleSelection(prev, event))}
                        onSelectAllEmail={handleSelectAll(setSelectedEmail)}
                        onSelectAllPush={handleSelectAll(setSelectedPush)}
                    />
                </div>
                <section className={styles.scheduleSection} aria-labelledby="notification-schedule-title">
                    <div className={styles.scheduleSectionHeader}>
                        <h3 id="notification-schedule-title" className={styles.scheduleTitle}>
                            {t('bulkEditSettings.notifications.schedule.title')}
                        </h3>
                        <Button type="button" variant="secondary" onClick={openScheduleEditor}>
                            {t('bulkEditSettings.notifications.schedule.edit')}
                        </Button>
                    </div>

                    <div className={styles.scheduleTable} role="table" aria-label={t('bulkEditSettings.notifications.schedule.title')}>
                        <div className={styles.scheduleTableHeader} role="row">
                            <div className={`${styles.scheduleNameCell} ${styles.scheduleHeaderCell}`} role="columnheader">
                                {t('bulkEditSettings.notifications.schedule.name')}
                            </div>
                            <div className={`${styles.schedulePreviewCell} ${styles.scheduleHeaderCell}`} role="columnheader">
                                {t('bulkEditSettings.notifications.schedule.preview')}
                            </div>
                        </div>
                        {savedSchedule.length === 0 ? (
                            <div className={styles.scheduleEmptyRow} role="row">
                                <div className={styles.scheduleEmptyMessage} role="cell">
                                    {t('bulkEditSettings.notifications.schedule.empty')}
                                </div>
                            </div>
                        ) : (
                            <div className={styles.scheduleSavedRow} role="row">
                                <div className={styles.scheduleSavedCell} role="cell">
                                    {t('bulkEditSettings.notifications.schedule.title')}
                                </div>
                                <div className={styles.scheduleSavedCell} role="cell">
                                    <div className={styles.schedulePreview}>
                                        {WEEK_DISPLAY_DAYS.map(({ key, day }) => {
                                            const dayEvents = savedSchedule.filter(event =>
                                                new Date(String(event.start)).getDay() === day
                                            );
                                            const periods = dayEvents.map(event =>
                                                `${String(event.start).slice(11, 16)}–${String(event.end).slice(11, 16)}`
                                            );
                                            const dayLabel = t(`bulkEditSettings.recording.days.short.${key}`);
                                            const tooltip = periods.length
                                                ? periods.join(', ')
                                                : t('bulkEditSettings.notifications.schedule.noScheduleDay');

                                            return (
                                                <div
                                                    key={key}
                                                    className={styles.dayColumn}
                                                    title={t('bulkEditSettings.notifications.schedule.dayTooltip', {
                                                        day: t(`bulkEditSettings.recording.days.full.${key}`),
                                                        periods: tooltip,
                                                    })}
                                                >
                                                    <span className={styles.dayLabel}>{dayLabel}</span>
                                                    <div className={styles.dayBar}>
                                                        {dayEvents.map((event, index) => {
                                                            const start = timeToFraction(String(event.start).slice(11, 19));
                                                            const end = timeToFraction(String(event.end).slice(11, 19));
                                                            return (
                                                                <div
                                                                    key={`${event.id}-${index}`}
                                                                    className={styles.dayBarSegment}
                                                                    style={{ top: `${start * 100}%`, height: `${Math.max(0, end - start) * 100}%` }}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

        </>
    )
}
export default NotificationSetting